import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Modal, notification } from 'antd';
import { useEventStore } from '../../store/event-store';
import { MoreButton } from '../more-button';
import { ServerSideDatatable } from '../serverside-datatable';
import { EventFilterer } from './event-filterer';
import QRCodeGenerator from '../../components/qr-code';
import { useAuthStore } from '../../store/auth-store';
import { Role } from '../../services/auth-service/types';

interface Props {}
export const EventList: React.FC<Props> = () => {
  const [api, contextHolder] = notification.useNotification();
  const { idToken } = useAuthStore();
  const {
    currentPage,
    loadingEvents,
    events,
    listEvents,
    totalCount,
    deleteEvent,
    downloadCsv,
  } = useEventStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [event, setEvent] = useState<any>();
  const [refreshKey, setRefreshKey] = useState(0);
  const customerUrl = String(window.location.origin).replace(
    'heartmap-admin',
    'heartmap-onsite',
  );
  const refreshTable = () => {
    setRefreshKey(oldKey => oldKey + 1);
  };
  const navigate = useNavigate();
  const navigateToEvent = useCallback(
    (id: number) => {
      navigate(`/events/${id}`);
    },
    [navigate],
  );
  const navigateToEventEdit = useCallback(
    (id: number) => {
      navigate(`/events/${id}/edit`);
    },
    [navigate],
  );
  const deleteEventCallback = useCallback(
    async (id: number) => {
      await deleteEvent({ id });
      // await listEvents({ currentPage });
      refreshTable();
      setShowDeleteModal(false);
    },
    [navigate],
  );

  const currentLoggedInUser = useMemo(() => idToken?.payload, [idToken]);

  const isSuperUser = useMemo(() => {
    if (!currentLoggedInUser?.['custom:roles']) return false;
    const roles = currentLoggedInUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.SuperUser];
    let canAccess = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canAccess = true;
      }
    });
    return canAccess;
  }, [currentLoggedInUser]);

  const isEventManager = useMemo(() => {
    if (!currentLoggedInUser?.['custom:roles']) return false;
    const roles = currentLoggedInUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.EventManager];
    let canAccess = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canAccess = true;
      }
    });
    return canAccess;
  }, [currentLoggedInUser]);

  return (
    <>
      {contextHolder}
      <ServerSideDatatable
        key={refreshKey}
        defaultOrderReference="createdAt"
        searchBarPlaceholder="Search by event name"
        columns={[
          {
            id: 'id',
            name: <h5 className="text-gray font-medium text-base">ID</h5>,
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => (
              <Link
                className="text-sm font-medium text-blue hover:underline"
                to={`/events/${row.eventId}`}>
                {' '}
                {row.eventId}{' '}
              </Link>
            ),
            grow: 0.5,
            sortable: true,
            sortField: 'id',
          },
          {
            id: 'name',
            name: <h5 className="text-gray font-medium text-base">Name</h5>,
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => (
              <Link
                className="text-sm font-medium text-blue hover:underline"
                to={`/events/${row.eventId}`}>
                {' '}
                {row.name}{' '}
              </Link>
            ),
            grow: 2,
            sortable: true,
            sortField: 'name',
          },
          {
            id: 'slug',
            name: <h5 className="text-gray font-medium text-base">Slug</h5>,
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => row.slug,
            grow: 2,
            sortable: true,
            sortField: 'slug',
          },
          {
            id: 'orderPrefix',
            name: (
              <h5 className="text-gray font-medium text-base">Order Prefix</h5>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => row.orderPrefix,
            grow: 1.5,
            sortable: true,
            sortField: 'orderPrefix',
          },
          {
            id: 'biomarkers',
            name: (
              <h5 className="text-gray font-medium text-base">Biomarkers</h5>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row =>
              row.biomarkers.map(marker => (
                <>
                  &bull; {marker}
                  <br />
                </>
              )),
            grow: 2.5,
            sortable: true,
            sortField: 'biomarkers',
          },
          {
            id: 'testCode',
            name: (
              <h5 className="text-gray font-medium text-base">Test Code</h5>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => row.testCode,
            grow: 1,
            sortable: true,
            sortField: 'testCode',
          },
          {
            id: 'clientId',
            name: (
              <h5 className="text-gray font-medium text-base">Client ID</h5>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => row.clientId,
            grow: 1,
            sortable: true,
            sortField: 'clientId',
          },
          {
            id: 'date',
            name: (
              <h5 className="text-gray font-medium text-base">Event Date</h5>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => (
              <div className="flex flex-col font-medium text-sm">
                <div>{DateTime.fromISO(row.date).toFormat('LL-dd-yyyy')}</div>
              </div>
            ),
            grow: 1.5,
            sortable: true,
            sortField: 'date',
          },
          {
            id: 'createdAt',
            name: (
              <h5 className="text-gray font-medium text-base">Created At</h5>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => (
              <div className="flex flex-col font-medium text-sm">
                <div>
                  {DateTime.fromISO(row.createdAt).toFormat('LL-dd-yyyy')}
                </div>
                <div>{DateTime.fromISO(row.createdAt).toFormat('hh:mm a')}</div>
              </div>
            ),
            grow: 1.5,
            sortable: true,
            sortField: 'createdAt',
          },
          {
            id: 'actions',
            name: '',
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: row => (
              <MoreButton>
                <button
                  type="button"
                  className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                  onClick={() => {
                    if (row.eventId) navigateToEvent(row.eventId);
                  }}>
                  View Event
                </button>
                {(isSuperUser || isEventManager) && (
                  <button
                    type="button"
                    className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                    onClick={() => {
                      if (row.eventId) navigateToEventEdit(row.eventId);
                    }}>
                    Edit Event
                  </button>
                )}
                {isSuperUser && (
                  <button
                    type="button"
                    className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                    onClick={() => {
                      setEvent(row);
                      setShowDeleteModal(true);
                    }}>
                    Delete
                  </button>
                )}

                <button
                  type="button"
                  className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                  onClick={() => {
                    setEvent(row);
                    setShowQRModal(true);
                  }}>
                  QR Code
                </button>
                <button
                  type="button"
                  className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                  onClick={async () => {
                    try {
                      await downloadCsv(row.eventId);
                    } catch (e) {
                      api.error({ message: 'Not available...' });
                    }
                  }}>
                  Download Orders
                </button>
              </MoreButton>
            ),
            grow: 1,
          },
        ]}
        data={events}
        currentPage={currentPage}
        paginationTotalRows={totalCount}
        progressPending={loadingEvents}
        serverAction={listEvents}
        filterComponent={<EventFilterer />}
      />
      <Modal
        title="Confirm Event Deletion"
        visible={showDeleteModal}
        onOk={() => {
          deleteEventCallback(event.eventId);
        }}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText="Yes"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}>
        <p>{`Are you sure you want to delete event #${event?.eventId}?`}</p>
        <i>{`Note: You can't reuse the slug '${event?.slug}' and the order prefix '${event?.orderPrefix}' again once it was deleted.`}</i>
      </Modal>
      <Modal
        title="QR Generator"
        visible={showQRModal}
        footer={null}
        onCancel={() => {
          setShowQRModal(false);
        }}>
        <div className="align-center">
          <QRCodeGenerator url={`${customerUrl}/registration/${event?.slug}`} />
        </div>
      </Modal>
    </>
  );
};
